import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import Grid from '../common/Grid';
import {
  WaterDrop as WaterDropIcon,
  Thermostat as ThermostatIcon,
  ElectricBolt as ElectricBoltIcon,
  Air as AirIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useRealTimeWaterQuality } from '../../hooks/useDataApi';

interface RealTimeWaterQualityMonitorProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  onParameterAlert?: (parameter: string, value: number, status: 'warning' | 'error') => void;
}

interface ParameterConfig {
  label: string;
  unit: string;
  icon: React.ReactNode;
  color: string;
  ranges: {
    optimal: [number, number];
    acceptable: [number, number];
    warning: [number, number];
  };
}

const parameterConfigs: Record<string, ParameterConfig> = {
  ph: {
    label: 'pH Level',
    unit: '',
    icon: <WaterDropIcon />,
    color: '#2196f3',
    ranges: {
      optimal: [5.5, 6.5],
      acceptable: [5.0, 7.0],
      warning: [4.0, 8.0]
    }
  },
  ec: {
    label: 'Electrical Conductivity',
    unit: 'mS/cm',
    icon: <ElectricBoltIcon />,
    color: '#ff9800',
    ranges: {
      optimal: [1.2, 1.8],
      acceptable: [0.8, 2.2],
      warning: [0.3, 3.0]
    }
  },
  temperature: {
    label: 'Temperature',
    unit: '°C',
    icon: <ThermostatIcon />,
    color: '#f44336',
    ranges: {
      optimal: [18, 24],
      acceptable: [15, 28],
      warning: [10, 35]
    }
  },
  dissolvedOxygen: {
    label: 'Dissolved Oxygen',
    unit: 'mg/L',
    icon: <AirIcon />,
    color: '#4caf50',
    ranges: {
      optimal: [6, 8],
      acceptable: [5, 9],
      warning: [3, 12]
    }
  }
};

const RealTimeWaterQualityMonitor: React.FC<RealTimeWaterQualityMonitorProps> = ({
  autoRefresh = true,
  refreshInterval = 5000,
  onParameterAlert
}) => {
  const [isAutoRefresh, setIsAutoRefresh] = useState(autoRefresh);
  const [alerts, setAlerts] = useState<Array<{id: string, parameter: string, message: string, severity: 'warning' | 'error'}>>([]);
  
  const { data, loading, error, refresh } = useRealTimeWaterQuality(
    isAutoRefresh ? refreshInterval : 0
  );

  // Check for parameter alerts
  useEffect(() => {
    if (!data || data.status === 'offline') return;

    const newAlerts: typeof alerts = [];

    Object.entries(parameterConfigs).forEach(([key, config]) => {
      const value = data[key as keyof typeof data] as number;
      if (typeof value !== 'number') return;

      const { optimal, acceptable, warning } = config.ranges;
      
      let alertSeverity: 'warning' | 'error' | null = null;
      let message = '';

      if (value < warning[0] || value > warning[1]) {
        alertSeverity = 'error';
        message = `${config.label} is critically out of range: ${value}${config.unit}`;
      } else if (value < acceptable[0] || value > acceptable[1]) {
        alertSeverity = 'warning';
        message = `${config.label} is outside acceptable range: ${value}${config.unit}`;
      }

      if (alertSeverity) {
        newAlerts.push({
          id: `${key}-${Date.now()}`,
          parameter: key,
          message,
          severity: alertSeverity
        });

        if (onParameterAlert) {
          onParameterAlert(key, value, alertSeverity);
        }
      }
    });

    setAlerts(newAlerts);
  }, [data, onParameterAlert]);

  const getParameterStatus = (parameter: string, value: number) => {
    const config = parameterConfigs[parameter];
    if (!config) return 'unknown';

    const { optimal, acceptable, warning } = config.ranges;
    
    if (value >= optimal[0] && value <= optimal[1]) return 'optimal';
    if (value >= acceptable[0] && value <= acceptable[1]) return 'acceptable';
    if (value >= warning[0] && value <= warning[1]) return 'warning';
    return 'critical';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'optimal':
        return <CheckCircleIcon color="success" />;
      case 'acceptable':
        return <CheckCircleIcon color="info" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'critical':
        return <ErrorIcon color="error" />;
      default:
        return <ErrorIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'optimal': return '#4caf50';
      case 'acceptable': return '#2196f3';
      case 'warning': return '#ff9800';
      case 'critical': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getProgressValue = (parameter: string, value: number) => {
    const config = parameterConfigs[parameter];
    if (!config) return 0;

    const { warning } = config.ranges;
    const range = warning[1] - warning[0];
    const normalizedValue = ((value - warning[0]) / range) * 100;
    return Math.max(0, Math.min(100, normalizedValue));
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load real-time water quality data: {error instanceof Error ? error.message : String(error)}
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Real-Time Water Quality Monitor
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isAutoRefresh}
                onChange={(e) => setIsAutoRefresh(e.target.checked)}
                color="primary"
              />
            }
            label="Auto Refresh"
          />
          
          <Tooltip title="Refresh Now">
            <IconButton onClick={refresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Chip
            label={data?.status === 'online' ? 'Online' : 'Offline'}
            color={data?.status === 'online' ? 'success' : 'error'}
            size="small"
          />
        </Box>
      </Box>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {alerts.map((alert) => (
            <Alert key={alert.id} severity={alert.severity} sx={{ mb: 1 }}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Parameters Grid */}
      <Grid container spacing={3}>
        {Object.entries(parameterConfigs).map(([key, config]) => {
          const value = data?.[key as keyof typeof data] as number;
          const status = getParameterStatus(key, value || 0);
          const progressValue = getProgressValue(key, value || 0);

          return (
            <Grid xs={12} sm={6} md={3} key={key}>
              <Card 
                elevation={1}
                sx={{ 
                  height: '100%',
                  border: `2px solid ${getStatusColor(status)}`,
                  transition: 'all 0.3s ease'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: config.color, mr: 1 }}>
                      {config.icon}
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {config.label}
                    </Typography>
                    <Box sx={{ ml: 'auto' }}>
                      {getStatusIcon(status)}
                    </Box>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                    {loading ? '---' : (value?.toFixed(key === 'ph' ? 1 : 2) || '---')}
                    <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                      {config.unit}
                    </Typography>
                  </Typography>
                  
                  <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mb: 1,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getStatusColor(status)
                      }
                    }}
                  />
                  
                  <Typography variant="caption" color="text.secondary">
                    Status: {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Last Updated */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Last updated: {data?.timestamp ? new Date(data.timestamp).toLocaleString() : 'Never'}
      </Typography>
    </Paper>
  );
};

export default RealTimeWaterQualityMonitor;
